import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  HandHeart, Mail, Save, Trash2, Archive, Send, Inbox as InboxIcon,
  Clock, CheckCircle2, AlertCircle, Loader2, Calendar,
} from "lucide-react";

/* /dashboard/prayer-inbox  (admin-only)
   Mike's nightly tool:
     • Top card  → edits "Word From Mike" for TODAY (mirrors Command Center)
     • Bottom    → inbox of prayer requests; filter by status; respond + archive/delete
   Wires to existing backend endpoints:
     GET    /api/dashboard/daily-message
     PUT    /api/admin/dashboard/daily-message       { note }
     GET    /api/admin/prayer-requests
     PUT    /api/admin/prayer-requests/{pid}/respond { response }
     PUT    /api/admin/prayer-requests/{pid}/archive
     DELETE /api/admin/prayer-requests/{pid}
*/

const STATUS_META = {
  new:      { label: "New",      tone: "#F39A1F", icon: AlertCircle  },
  answered: { label: "Answered", tone: "#2E9F3A", icon: CheckCircle2 },
  archived: { label: "Archived", tone: "#0F172A", icon: Archive      },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

/* ============== DAILY MESSAGE CARD ============== */
function DailyMessageEditor() {
  const [note, setNote] = useState("");
  const [verse, setVerse] = useState(null);
  const [savedNote, setSavedNote] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/daily-message");
      const d = r.data || {};
      setNote(d.mike_note || "");
      setSavedNote(d.mike_note || "");
      setVerse(d.verse || null);
      setDate(d.mike_note_date || "");
    } catch (e) {
      toast.error("Couldn't load today's message.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = note !== savedNote;
  const overLimit = note.length > 600;

  const save = async () => {
    if (overLimit) return toast.error("Keep it under 600 characters.");
    setBusy(true);
    try {
      const r = await api.put("/admin/dashboard/daily-message", { note });
      setSavedNote(r.data.note || "");
      toast.success("Today's message is live on the Command Center.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="daily-message-editor"
      className="rounded-2xl border-2 shadow-xl overflow-hidden"
      style={{ borderColor: "#F39A1F", background: "linear-gradient(135deg,#0F172A 0%,#1A2E1F 100%)" }}
    >
      <div className="p-6 sm:p-8 text-white">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#F39A1F" }}>
          <Mail className="w-3.5 h-3.5" /> ★ Word From Mike · Today's Note
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-2 leading-tight" style={{ textWrap: "balance" }}>
          What do you want your members to read first?
        </h2>
        <p className="text-white/80 mt-2 text-sm">
          This note appears at the top of every member's Command Center for {date || "today"}. Write a short encouragement,
          a verse-of-the-day takeaway, or a personal nudge. Max 600 characters.
        </p>

        {verse && (
          <div className="mt-4 rounded-xl px-4 py-3 text-sm" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(243,154,31,0.35)" }}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: "#F39A1F" }}>
              Today's NKJV Verse · {verse.reference}
            </div>
            <div className="italic text-white/90" style={{ textWrap: "balance" }}>"{verse.text}"</div>
          </div>
        )}

        <div className="mt-5">
          <textarea
            data-testid="daily-message-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={loading || busy}
            rows={5}
            maxLength={700}
            placeholder="Good morning, family. Today, remember that…"
            className="w-full rounded-xl bg-white/95 text-foreground p-4 text-base leading-relaxed border-2 border-transparent focus:border-[#F39A1F] outline-none resize-y"
          />
          <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
            <div className={`text-xs ${overLimit ? "text-red-300 font-bold" : "text-white/65"}`} data-testid="daily-message-counter">
              {note.length} / 600 characters
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="daily-message-revert-btn"
                onClick={() => setNote(savedNote)}
                disabled={!dirty || busy}
                className="text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-white/30 text-white/80 hover:bg-white/10 disabled:opacity-40"
              >
                Revert
              </button>
              <button
                data-testid="daily-message-save-btn"
                onClick={save}
                disabled={!dirty || busy || overLimit || loading}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-md disabled:opacity-40"
                style={{ background: "#F39A1F", color: "#0F172A" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {busy ? "Saving…" : dirty ? "Save Today's Note" : "Saved"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============== PRAYER ROW ============== */
function PrayerRow({ p, onRespond, onArchive, onDelete }) {
  const [reply, setReply] = useState(p.mike_response || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(p.status === "new");
  const meta = STATUS_META[p.status] || STATUS_META.new;
  const Icon = meta.icon;
  const replyDirty = reply.trim() !== (p.mike_response || "").trim();

  const sendReply = async () => {
    const txt = reply.trim();
    if (txt.length < 2) return toast.error("Write a short reply first.");
    setBusy(true);
    try {
      await onRespond(p.id, txt);
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`prayer-row-${p.id}`}
      className="rounded-xl border-2 bg-white shadow-md overflow-hidden"
      style={{ borderColor: meta.tone + "55" }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 sm:p-5 flex items-start gap-4 hover:bg-gold/5"
      >
        <span
          className="inline-flex items-center gap-1.5 shrink-0 mt-0.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold text-white"
          style={{ background: meta.tone }}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground truncate">
            {p.subject || "(No subject)"} · <span className="text-foreground/65 font-normal">{p.name || "Anonymous"}</span>
          </div>
          <div className="text-xs text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(p.created_at)}</span>
            {p.email && <span className="truncate max-w-[200px]">{p.email}</span>}
            {p.private && <span className="text-red-wine font-bold">· PRIVATE</span>}
          </div>
          <div className={`mt-2 text-sm text-foreground/85 ${open ? "" : "line-clamp-2"}`} style={{ textWrap: "pretty" }}>
            {p.body}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-gold/30 bg-gold/5 p-4 sm:p-5 space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-red-wine">
              Your Reply (visible only to Mike — for your records)
            </label>
            <textarea
              data-testid={`prayer-reply-${p.id}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder="Praying with you, friend. Here's a verse the Lord laid on my heart…"
              className="w-full mt-1.5 rounded-lg border-2 border-border bg-white p-3 text-sm leading-relaxed focus:border-[#F39A1F] outline-none resize-y"
            />
            <div className="text-[11px] text-foreground/55 mt-1">{reply.length} / 4000 · {p.responded_at ? `Last saved ${fmtDate(p.responded_at)}` : "Not yet answered"}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              data-testid={`prayer-send-${p.id}`}
              onClick={sendReply}
              disabled={busy || !replyDirty}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-md disabled:opacity-40"
              style={{ background: "#2E9F3A", color: "white" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {p.status === "answered" ? "Update Reply" : "Save Reply · Mark Answered"}
            </button>
            {p.status !== "archived" && (
              <button
                data-testid={`prayer-archive-${p.id}`}
                onClick={() => onArchive(p.id)}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-2 rounded-md border-2 border-foreground/20 hover:bg-foreground/5"
              >
                <Archive className="w-4 h-4" /> Archive
              </button>
            )}
            <button
              data-testid={`prayer-delete-${p.id}`}
              onClick={() => {
                if (window.confirm("Delete this prayer request permanently? This cannot be undone.")) onDelete(p.id);
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-2 rounded-md border-2 border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== MAIN TAB ============== */
export default function PrayerInboxTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new"); // new | answered | archived | all

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prayer-requests");
      setRequests(r.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load prayer requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { new: 0, answered: 0, archived: 0 };
    for (const r of requests) c[r.status || "new"] = (c[r.status || "new"] || 0) + 1;
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter((r) => (r.status || "new") === filter);
  }, [requests, filter]);

  const respondTo = async (pid, response) => {
    try {
      await api.put(`/admin/prayer-requests/${pid}/respond`, { response });
      setRequests((rs) => rs.map((r) => (r.id === pid ? { ...r, mike_response: response, status: "answered", responded_at: new Date().toISOString() } : r)));
      toast.success("Marked as answered.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save reply."); }
  };

  const archive = async (pid) => {
    try {
      await api.put(`/admin/prayer-requests/${pid}/archive`);
      setRequests((rs) => rs.map((r) => (r.id === pid ? { ...r, status: "archived" } : r)));
      toast.success("Archived.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't archive."); }
  };

  const remove = async (pid) => {
    try {
      await api.delete(`/admin/prayer-requests/${pid}`);
      setRequests((rs) => rs.filter((r) => r.id !== pid));
      toast.success("Deleted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  const FILTERS = [
    { key: "new",      label: "New",      count: counts.new },
    { key: "answered", label: "Answered", count: counts.answered },
    { key: "archived", label: "Archived", count: counts.archived },
    { key: "all",      label: "All",      count: requests.length },
  ];

  return (
    <div data-testid="prayer-inbox-tab" className="space-y-8">
      <DailyMessageEditor />

      <div className="rounded-2xl bg-white border-2 border-border shadow-md overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border/60 bg-white">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-red-wine">
            <HandHeart className="w-3.5 h-3.5" /> ★ Prayer Inbox
          </div>
          <h2 className="font-serif-display text-3xl text-red-wine mt-1 flex items-center gap-3">
            Your Members Need Prayer
            <span className="text-base align-middle text-foreground/60 font-sans">
              ({requests.length} total · <span className="text-[#F39A1F] font-bold">{counts.new} new</span>)
            </span>
          </h2>
          <p className="text-sm text-foreground/75 mt-1">
            Submissions from the Command Center Prayer Box. Click any card to expand, write your private reply for your records,
            then mark it answered. Archive when done; delete spam.
          </p>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="prayer-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`prayer-filter-${f.key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border-2 transition-all ${
                  filter === f.key
                    ? "bg-[#2E9F3A] text-white border-[#2E9F3A] shadow"
                    : "bg-white text-foreground/80 border-border hover:bg-gold/10"
                }`}
              >
                {f.label} <span className="ml-1 opacity-80">({f.count})</span>
              </button>
            ))}
            <button
              onClick={load}
              data-testid="prayer-refresh"
              className="ml-auto px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border-2 border-border text-foreground/80 hover:bg-gold/10"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-3 bg-ivory">
          {loading ? (
            <div className="text-center py-10 text-foreground/60" data-testid="prayer-loading">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Loading prayer requests…
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-foreground/65 rounded-xl bg-white border-2 border-dashed border-border" data-testid="prayer-empty">
              <InboxIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <div className="font-serif-display text-xl text-red-wine">No prayer requests in this view.</div>
              <p className="text-sm mt-1">When your members hit "Submit Prayer" on the Command Center, it lands right here.</p>
            </div>
          ) : (
            visible.map((p) => (
              <PrayerRow
                key={p.id}
                p={p}
                onRespond={respondTo}
                onArchive={archive}
                onDelete={remove}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
